import request from '../../../../../api/request';
import { actionKitImpFunction } from '../../../../../components/ozmoit/common';
import {
  PaginationQueryDto,
  PaginationRequestType,
} from '../../../../../types/paginationRequestType';
import { baseToSelectOption } from '../../../../academicOffers/courses/utils';
import { months } from '../../../../Configurations/academicOffer/utils';
import {
  StudentObservationLoaderParams,
  StudentObservationsListResponse,
  StudentObservationsParams,
  StudentResponse,
  StudentParams,
  StudentObservationsFiltersResponse,
  StudentObservationsFormResponse,
} from '../types';

/**
 * Obtiene los filtros de la tabla de observaciones
 */
const getStudentObservationTableAction = actionKitImpFunction(async () => {
  const response = await request<StudentObservationsFiltersResponse>(
    `maintainers-portal/student-management/student-observations/filter-data`,
  );
  if (response.error) {
    throw new Error('Error fetching student warnings form data');
  }

  const years = Array.from(
    new Set(response.data?.periods?.map((row) => row?.academicYear)),
  ).map((year) => ({
    name: `${year}`,
    id: year,
  }));

  return {
    years: baseToSelectOption(years),
    months: baseToSelectOption(
      months.map((e) => ({ name: e.label, id: Number(e.value) })),
    ),
  };
});

/**
 * Obtiene las opciones para el formulario de observaciones
 */
const getStudentObservationFormAction = actionKitImpFunction(async () => {
  const response = await request<StudentObservationsFormResponse>(
    `maintainers-portal/student-management/student-observations/form-data`,
  );
  if (response.error) {
    throw new Error('Error fetching student warnings form data');
  }

  const { courses, types } = response.data;
  return {
    courses: baseToSelectOption(courses),
    types: baseToSelectOption(types),
  };
});

/**
 * Obtiene la informacion de un estudiante
 */
const getStudentAction = actionKitImpFunction(
  async ({ studentId }: StudentParams) => {
    const response = await request<StudentResponse>(
      `maintainers-portal/student-management/student-observations/${studentId}`,
    );
    if (response.error) {
      throw new Error('Error fetching student warnings form data');
    }
    return response.data;
  },
);

/**
 * Obtiene una observacion de un estudiante
 */
const getStudentObservationAction = actionKitImpFunction(
  async ({ studentId, observationId }: StudentObservationLoaderParams) => {
    const response = await request<any>(
      `maintainers-portal/student-management/student-observations/${studentId}/observation/${observationId}`,
    );
    if (response.error) {
      throw new Error('Error fetching student warnings form data');
    }
    return response.data;
  },
);

/**
 * Obtiene las observaciones de un estudiante
 */
const getStudentObservationsAction = actionKitImpFunction(
  async ({
    page,
    ...params
  }: PaginationQueryDto<StudentObservationsParams>): Promise<
    PaginationRequestType<StudentObservationsListResponse>
  > => {
    const response = await request<
      PaginationRequestType<StudentObservationsListResponse>
    >(
      `maintainers-portal/student-management/student-observations/${params.studentId}/observation`,
      {
        params: {
          ...params,
          page: Math.max(page - 1, 0),
        },
      },
    );
    if (response.error) {
      throw new Error('Error fetching student warnings summary data');
    }
    return response.data;
  },
);

/**
 * Create observation
 */
const postCreateObservationAction = actionKitImpFunction(
  async (
    studentId: number,
    data: {
      type: string;
      courseId: number | null;
      observation: string;
      fileId: number | null;
    },
  ) => {
    const response = await request<void>(
      `maintainers-portal/student-management/student-observations/${studentId}/observation`,
      {
        method: 'POST',
        data,
      },
    );
    if (response.error) {
      throw new Error('Error saving student data');
    }
  },
);

/**
 * Download document complementario from observation
 */
const getStudentObservationDocumentDownloadAction = actionKitImpFunction(
  async ({
    studentId,
    observationId,
  }: {
    studentId: number;
    observationId: number;
  }) => {
    const response = await request<{ url: string }>(
      `maintainers-portal/student-management/student-observations/${studentId}/observation/${observationId}/download`,
      {
        method: 'get',
      },
    );
    if (response.error) {
      throw new Error('Error saving student data');
    }
    return response.data;
  },
);

/**
 * Download observations summary
 */
const exportSummaryAction = actionKitImpFunction(
  async (params: StudentObservationsParams) => {
    const response = await request<BlobPart>(
      `maintainers-portal/student-management/student-observations/${params.studentId}/observations/download`,
      {
        responseType: 'blob',
        params: {
          ...params,
        },
      },
    );
    if (response.error) {
      throw new Error('Error fetching student data');
    }
    return response.data;
  },
);

export {
  getStudentObservationDocumentDownloadAction,
  getStudentObservationFormAction,
  getStudentObservationTableAction,
  getStudentAction,
  getStudentObservationAction,
  getStudentObservationsAction,
  postCreateObservationAction,
  exportSummaryAction,
};
